import FF from "@/components/forms/FormField.module.css";
import RA from "@/components/forms/Radio.module.css";
import RG from "@/components/forms/Radiogroup.module.css";
import TF from "@/components/forms/TextField.module.css";

import { HeroBlock } from "@/components/HeroBlock.tsx";
import LR from "@/components/LeftRight.module.css";
import { LeftRight } from "@/components/LeftRight.tsx";
import { Separator } from "@/components/Separator.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { Toolbar } from "@/components/Toolbar.tsx";
import { VatNumberText } from "@/components/VatText.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { WToolButton } from "@/components/button/WToolButton.tsx";
import { FormField, type FormFieldProps } from "@/components/forms/FormField.tsx";
import { FormActions, FormSubmitButton } from "@/components/forms/FormSubmitButton.tsx";
import { WCheckbox } from "@/components/forms/WCheckbox.tsx";
import { WHookForm } from "@/components/forms/WHookForm.tsx";
import { WSelect } from "@/components/forms/WSelect.tsx";
import { WTextField, type WTextFieldProps } from "@/components/forms/WTextField.tsx";
import { controllerProps, formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { useAriaField } from "@/components/forms/ariaFieldRegister.ts";
import { ContentLoadingSuspense } from "@/components/loading/Loading.tsx";
import { useConfig } from "@/config.ts";
import { testingLibraryContext } from "@/dev/BrowserTestContext.testing-library.ts";
import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { FirstAccountNotices } from "@/modules/billing/FirstAccountNotices.tsx";
import { accountCreateInteract } from "@/modules/billing/billingAccount/BillingCreate.test-utils.tsx";
import { baFieldsConfigAtom, type EditableFieldName } from "@/modules/billing/billingAccount/fieldsConfig.ts";
import { baLabels, t_County } from "@/modules/billing/billingAccount/fieldsInfo.tsx";
import {
    createBillingAccountMutation,
    updateBillingAccountMutation,
    useBillingAccount,
    type BillingAccountWithType,
} from "@/modules/billing/billingAccountQuery.ts";
import { countryRegionsConfigQueryAtom } from "@/modules/billing/billingConfigQuery.ts";
import { billingStateAtom, EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { AddMethodResult } from "@/modules/billing/choose_method/AddMethodParams.ts";
import { ChooseMethodContent } from "@/modules/billing/choose_method/ChooseMethod.tsx";
import { makeApplyForInvoicePaymentRequest } from "@/modules/billing/choose_method/addMethodUtils.ts";
import { invoiceAddNoticeAtom, invoiceApplyMessage } from "@/modules/billing/choose_method/invoiceMethodLogic.tsx";
import { raisePaymentAddError, useAddMethodWithProcessors } from "@/modules/billing/choose_method/useAddMethod.tsx";
import { FileWithSourceInput, type FileWithSource } from "@/modules/billing/forms/FileWithSourceInput.tsx";
import { billingAccountLink } from "@/modules/billing/links.ts";
import { addableMethodsAtom } from "@/modules/billing/paymentProcessorsLogic.tsx";
import { referralCodeAtom } from "@/modules/billing/referral/referralLogic.ts";
import { CountrySelect } from "@/modules/location/CountrySelect.tsx";
import { useThemeProps } from "@/modules/theme/useTheme.ts";
import { userDataAtom } from "@/modules/user/userApi.ts";
import { cn } from "@/utils/classNames.ts";
import { isStorybook } from "@/utils/environment.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation } from "@tanstack/react-query";
import { Navigate } from "@tanstack/react-router";
import type {
    BillingAccountCreateFields,
    BillingAccountCreateTypeParam,
    BillingAccountField,
    BillingAccountId,
    BillingAccountUpdateFields,
} from "@warrenio/api-spec/spec.oats.gen";
import { editableFields } from "@warrenio/api-spec/spec/billing.editableFields";
import { getOwn } from "@warrenio/utils/collections/getOwnProperty";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";
import { useAtom, useAtomValue } from "jotai/react";
import { Fragment, useMemo, useState, type ReactNode } from "react";
import { Input, Radio, RadioGroup } from "react-aria-components";
import { Controller, useForm, useFormContext, type SubmitHandler, type UseFormProps } from "react-hook-form";
import { first, isEmpty, partition, pick } from "remeda";

/**
 * NB: Inconsistency in the Create API, the the input field is `client_email` but the returned object field is named
 * `email`.
 *
 * Pretend {@link BillingAccountCreateFields} looks like {@link BillingAccountUpdateFields} to reduce the amount of
 * special casing.
 *
 * (the inverse transformation is done in the `onSubmit` function)
 */
interface FixedBillingAccountCreateFields extends Omit<BillingAccountCreateFields, "client_email"> {
    email: BillingAccountCreateFields["client_email"];
}

/** A valid field name must be listed in the {@link editableFields} array. */
interface BillingFormInputs extends Pick<FixedBillingAccountCreateFields, EditableFieldName> {
    // This is handled by special code (not as an editable field)
    type: BillingAccountCreateFields["type"];
}

function useBillingForm(props: UseFormProps<BillingFormInputs>) {
    return useForm<BillingFormInputs>({ ...props });
}

function BillingCreateForm() {
    //#region Hooks
    const { t } = useLingui();
    const config = useConfig();
    const { siteCountry, billingAccountValidationActive, allowDebt } = config;
    const regionsConfig = useSuspenseQueryAtom(countryRegionsConfigQueryAtom);
    const fieldsConfig = useAtomValue(baFieldsConfigAtom);
    const initialStateList = getOwn(regionsConfig, siteCountry)?.states ?? [];

    const invoiceAddNotice = useAtomValue(invoiceAddNoticeAtom);
    const { applyForInvoiceMessageForMultiplePaymentMethods } = useThemeProps();

    const methods = useAtomValue(addableMethodsAtom);
    const [invoiceMethods, otherMethods] = useMemo(
        () => partition([...methods.values()], (m) => m.type === "invoice"),
        [methods],
    );
    const invoiceMethod = first(invoiceMethods);
    const userData = useSuspenseQueryAtom(userDataAtom);
    const adder = useAddMethodWithProcessors(otherMethods, { account: undefined });

    /** Display "Please select method" validation error */
    const [showMethodUnselected, setShowMethodUnselected] = useState(false);

    const [storedReferralCode, setStoredReferralCode] = useAtom(referralCodeAtom);
    const { canEnterReferralCode } = useSuspenseQueryAtom(billingStateAtom);
    const referral_code = !canEnterReferralCode ? undefined : storedReferralCode;

    const form = useBillingForm({
        defaultValues: {
            country: siteCountry,
            state_code: fieldsConfig.state_code ? first(initialStateList)?.code : undefined, // "state_code" field is defined in conf and there are states for the country
            referral_code,
            type: "other",
        },
    });

    const apiClient = useApiClient();

    const createMutation = useStandardMutation(createBillingAccountMutation);
    const accountWasCreated = createMutation.isSuccess;

    async function doCreateAccount(inputs: BillingFormInputs) {
        const site = userData.signup_site ?? "__missing__";

        // Rename `email` -> `client_email`
        const { email, ...rest } = inputs;
        const body: BillingAccountCreateFields = { client_email: email, site, ...rest };

        const accountData = await createMutation.mutateAsync({ body });

        // Clear referral code after successful creation
        if (canEnterReferralCode) {
            setStoredReferralCode(undefined);
        }

        return accountData;
    }

    /** Mutation `data` will be `null` if there is a failure */
    const fullMutation = useMutation({
        async mutationFn({ inputs }: { inputs: BillingFormInputs }) {
            // Skip creation if we are retrying adding a payment method
            const accountData: BillingAccountWithType = createMutation.isSuccess
                ? createMutation.data
                : await doCreateAccount(inputs);

            try {
                if (inputs.type === "invoice") {
                    await makeApplyForInvoicePaymentRequest(apiClient, accountData.id);
                } else if (!addLater) {
                    const result = await doAddPaymentMethod(accountData);

                    switch (result) {
                        case AddMethodResult.CANCELLED:
                        case AddMethodResult.VALIDATION_FAILED:
                            return null;
                        case AddMethodResult.SUCCESS:
                            return accountData;
                        default:
                            exhaustiveSwitchCheck(result);
                    }
                }
            } catch (e) {
                raisePaymentAddError(e);
                return null;
            }

            return accountData;
        },
    });

    async function doAddPaymentMethod(accountData: BillingAccountWithType) {
        const account = new EBillingAccount(accountData, config);

        return await adder.addPaymentMethod({ account });
    }

    const [addLater, setAddLater] = useState(billingAccountValidationActive);

    //#endregion

    if (fullMutation.isSuccess && fullMutation.data) {
        return <Navigate {...billingAccountLink(fullMutation.data)} />;
    }

    const onSubmit: SubmitHandler<BillingFormInputs> = async (inputs, event) => {
        console.debug("Creating billing account with", { inputs, addLater, adder, event });
        if (!addLater && inputs.type === "other") {
            if (!adder.hasSelectedMethod) {
                setShowMethodUnselected(true);
                return;
            }
            if (!(await adder.validate(null))) {
                return;
            }
        }

        try {
            return await fullMutation.mutateAsync({ inputs });
        } finally {
            adder.dispose();
        }
    };

    let applyForInvoiceMessage;
    if (adder.props.methods.length === 1) {
        applyForInvoiceMessage = applyForInvoiceMessageForMultiplePaymentMethods
            ? applyForInvoiceMessageForMultiplePaymentMethods
            : invoiceApplyMessage;
    } else if (adder.props.methods.length > 1) {
        applyForInvoiceMessage = applyForInvoiceMessageForMultiplePaymentMethods ?? (
            <>
                {invoiceApplyMessage}
                <br />
                {!billingAccountValidationActive ? invoiceAddNotice : null}
            </>
        );
    }

    const allowToApplyForInvoice = invoiceMethod?.canAddToAccount?.({ account: undefined, user: userData }) ?? false;

    return (
        <WHookForm form={form} onSubmit={onSubmit}>
            {import.meta.env.DEV && !isStorybook && (
                <Toolbar>
                    <WToolButton
                        label="Autofill"
                        icon="i-lucide:bug"
                        action={async () => await accountCreateInteract(testingLibraryContext(), "success").fillForm()}
                    />
                </Toolbar>
            )}

            {!accountWasCreated && (
                // Only show the billing form controls if the account has not been created yet (ie. not when we are retrying adding a payment method)
                <>
                    <BillingFormControls />
                    <Separator />
                </>
            )}

            <h2 className="font-size-title pt-4 pl-4">
                <Trans>Payment details</Trans>
            </h2>

            <div className="pl-4 pt-4">
                <div style={{ maxWidth: "calc(160px + 440px)" }}>
                    <FirstAccountNotices compact />
                </div>
            </div>

            {allowToApplyForInvoice && (
                <Controller
                    name="type"
                    control={form.control}
                    render={(p) => (
                        <FormField wide isRequired label={t`Payment type`}>
                            <RadioGroup
                                className={RG.RadioGroup}
                                aria-label="Payment type"
                                orientation="horizontal"
                                value={p.field.value}
                                onChange={(value) => {
                                    p.field.onChange(value as BillingAccountCreateTypeParam);
                                }}
                            >
                                <Radio className={RA.Radio} value="other">
                                    {allowDebt ? (
                                        <Trans>Payment method</Trans>
                                    ) : (
                                        <Trans>Pay-as-you-go (recommended)</Trans>
                                    )}
                                </Radio>
                                <Radio className={RA.Radio} value="invoice">
                                    <Trans>Apply for payment by invoice</Trans>
                                </Radio>
                            </RadioGroup>
                        </FormField>
                    )}
                />
            )}

            {form.watch("type") === "other" && (
                <FormField wide isRequired label={t`Method`}>
                    <div className="lightBlock" style={{ maxWidth: "calc(440px)" }}>
                        {billingAccountValidationActive ? (
                            <p className="pb-4">
                                <b>
                                    <Trans>
                                        For pay-as-you-go billing account you can select the payment method after the
                                        account is created and validated.
                                    </Trans>
                                </b>
                            </p>
                        ) : (
                            <p className="color-muted pb-4">
                                <Trans>
                                    No payment will be made currently. You can change your payment method at any time.
                                </Trans>
                            </p>
                        )}

                        <p>
                            {billingAccountValidationActive ? (
                                <Trans>Methods available after validation</Trans>
                            ) : (
                                <Trans>Choose method</Trans>
                            )}
                        </p>
                        <div className="py-3">
                            <Separator />
                        </div>

                        <ChooseMethodContent {...adder.props} isDisabled={addLater} />
                        {!addLater && !adder.hasSelectedMethod && showMethodUnselected && (
                            <div className={FF.FormFieldError}>
                                <Trans>Select a payment method</Trans>
                            </div>
                        )}

                        {!billingAccountValidationActive && (
                            <>
                                <div className="py-3">
                                    <Separator />
                                </div>
                                <WCheckbox
                                    isSelected={addLater}
                                    onChange={setAddLater}
                                    isDisabled={billingAccountValidationActive}
                                >
                                    <Trans>Add payment method later</Trans>
                                </WCheckbox>
                            </>
                        )}
                    </div>
                </FormField>
            )}

            {form.watch("type") === "invoice" && applyForInvoiceMessage && (
                <FormField wide label={" "}>
                    <div className="lightBlock" style={{ maxWidth: "calc(440px)" }}>
                        <p className="color-muted">{applyForInvoiceMessage}</p>
                    </div>
                </FormField>
            )}

            {canEnterReferralCode && !accountWasCreated && (
                <>
                    <Separator />
                    <BField name="referral_code" />
                </>
            )}

            <Separator />

            <FormActions>
                <FormSubmitButton>
                    {!accountWasCreated ? <Trans>Create</Trans> : <Trans>Add payment method</Trans>}
                </FormSubmitButton>
            </FormActions>
        </WHookForm>
    );
}

function BillingEditForm({ billingAccount }: { billingAccount: EBillingAccount }) {
    const { id, title, account } = billingAccount;
    //#region Hooks
    const { t } = useLingui();

    const fieldsConfig = useAtomValue(baFieldsConfigAtom);

    // NB: Only pick fields that can be sent to the API (to prevent form serialization etc. from erroring)
    const visibleFields = editableFields.filter((name) => fieldsConfig[name]?.visible);
    const defaultValues = pick(account, visibleFields);
    const form = useBillingForm({ defaultValues });

    const updateMutation = useStandardMutation(updateBillingAccountMutation);
    //#endregion

    const onSubmit: SubmitHandler<BillingFormInputs> = async (inputs) => {
        const body: BillingAccountUpdateFields = inputs;
        await updateMutation.mutateAsync({ id, body });
    };

    if (updateMutation.isSuccess) {
        return <Navigate {...billingAccountLink(updateMutation.data)} />;
    }

    return (
        <WHookForm form={form} onSubmit={onSubmit}>
            <Toolbar>
                <WButton
                    color="primary"
                    size="bar"
                    variant="ghost"
                    action={billingAccountLink(billingAccount)}
                    icon="jp-icon-close"
                >
                    <Trans>Cancel</Trans>
                </WButton>
            </Toolbar>
            <div className="pt-4 pl-4 pr-4">
                <ViewTitle title={t`Edit "${title}"`} />
            </div>

            <BillingFormControls isEdit />

            <Separator />

            <FormActions>
                <FormSubmitButton>
                    <Trans>Save</Trans>
                </FormSubmitButton>
                <WButton action={billingAccountLink(billingAccount)}>
                    <Trans>Cancel</Trans>
                </WButton>
            </FormActions>
        </WHookForm>
    );
}

function BillingFormControls({ isEdit = false }: { isEdit?: boolean }) {
    //#region Hooks
    const { t } = useLingui();
    const fieldsConfig = useAtomValue(baFieldsConfigAtom);
    const regionsConfig = useSuspenseQueryAtom(countryRegionsConfigQueryAtom);
    const { baFieldPresentation } = useThemeProps();
    const { control, setValue, watch } = useFormContext<BillingFormInputs>();

    //#endregion

    interface SectionField {
        field: EditableFieldName;
        element: ReactNode;
    }
    function makeSection(fields: SectionField[]) {
        return fields
            .filter((f) => fieldsConfig[f.field]?.visible)
            .map((f) => <Fragment key={f.field}>{f.element}</Fragment>);
    }

    const baseSection = makeSection([
        {
            field: "integration_id",
            element: <BField name="integration_id" />,
        },
    ]);

    const countryStateList = getOwn(regionsConfig, watch("country"))?.states ?? [];

    const addressSectionFields: SectionField[] = [
        {
            field: "country",
            element: (
                <Controller
                    name="country"
                    control={control}
                    {...controllerProps({ isRequired: fieldsConfig.country?.required })}
                    render={(p) => (
                        // XXX: This duplicates `BField` code, should use a common form field component
                        <FormField label={baLabels.country} {...formPropsToAriaProps(p)}>
                            <CountrySelect
                                isDisabled={p.field.disabled}
                                valueKey={p.field.value}
                                onChange={(country) => {
                                    const newValue = country.code.iso2;
                                    const states = regionsConfig[newValue]?.states ?? [];
                                    p.field.onChange(newValue);
                                    // Only keep state_code in sync when the field is configured for this site.
                                    // Otherwise ensure no hidden default state gets submitted.
                                    setValue("state_code", stateCodeFieldDefined ? first(states)?.code : undefined);
                                }}
                            />
                        </FormField>
                    )}
                />
            ),
        },
    ];

    const stateCodeFieldDefined = !!fieldsConfig.state_code;
    if (countryStateList.length > 0 && stateCodeFieldDefined) {
        addressSectionFields.push({
            field: "state_code",
            element: (
                <Controller
                    name="state_code"
                    control={control}
                    {...controllerProps({ isRequired: fieldsConfig.state_code?.required })}
                    render={(p) => (
                        // XXX: This duplicates `BField` code, should use a common form field component
                        <FormField label={baLabels.state_code} {...formPropsToAriaProps(p)}>
                            <WSelect
                                itemClassName={LR.item}
                                valueClassName={LR.value}
                                valueKey={p.field.value}
                                getKey={(item) => item.code}
                                items={countryStateList}
                                aria-label="Select state"
                                getTextValue={(item) => item.name}
                                onChange={(state) => p.field.onChange(state.code)}
                            >
                                {(item) => <LeftRight title={item.name} />}
                            </WSelect>
                        </FormField>
                    )}
                />
            ),
        });
    }

    addressSectionFields.push(
        {
            field: "county",
            element: (
                <BField
                    name="county"
                    className="w-65%"
                    label={countryStateList.length > 0 && stateCodeFieldDefined ? t_County : undefined} // Since we already show the "State" select box, only show the "County" label here.
                />
            ),
        },
        { field: "city", element: <BField name="city" className="w-65%" /> },
        { field: "address_line1", element: <BField name="address_line1" /> },
        { field: "address_line2", element: <BField name="address_line2" /> },
        { field: "post_index", element: <BField name="post_index" className="w-65%" /> },
    );
    const addressSection = makeSection(addressSectionFields);

    const invoiceSection = makeSection([
        { field: "company_name", element: <BField name="company_name" /> },
        { field: "company_reg_code", element: <BField name="company_reg_code" className="w-65%" /> },
        {
            field: "company_vat_number",
            element: <BField label={<VatNumberText />} name="company_vat_number" className="w-65%" />,
        },
        {
            field: "email",
            element: (
                <BField
                    type="email"
                    name="email"
                    isRequired
                    description={t`Email where invoices and reports are sent.`}
                />
            ),
        },
        { field: "customer_name", element: <BField name="customer_name" /> },
        { field: "customer_phone", element: <BField name="customer_phone" className="w-65%" /> },
        { field: "customer_id_number", element: <BField name="customer_id_number" className="w-65%" /> },
    ]);

    const documentSection = !isEdit
        ? makeSection([
              {
                  field: "document",
                  element: (
                      <Controller
                          name="document"
                          control={control}
                          {...controllerProps({ isRequired: fieldsConfig.document?.required })}
                          render={(p) => (
                              <FileFormField
                                  isRequired={!!fieldsConfig.document?.required}
                                  label={baLabels.document}
                                  onFieldChange={(file) => {
                                      p.field.onChange(file?.base64Source);
                                      setValue("document_name", file?.name);
                                  }}
                                  {...baFieldPresentation?.document}
                                  {...formPropsToAriaProps(p)}
                              />
                          )}
                      />
                  ),
              },
          ])
        : [];

    return (
        <>
            <BField
                name="title"
                isRequired
                description={t`This will only be used within the system so it's easy for you to recognize the account if you have several.`}
                autoFocus
            />
            {!isEmpty(baseSection) && baseSection}

            {!isEmpty(invoiceSection) && (
                <>
                    <h2 className="font-size-title pt-4 pl-4">
                        <Trans>Invoice details</Trans>
                    </h2>
                    {invoiceSection}
                </>
            )}

            {!isEmpty(addressSection) && (
                <>
                    <h2 className="font-size-title pt-4 pl-4">
                        <Trans>Address</Trans>
                    </h2>
                    {addressSection}
                </>
            )}

            {!isEmpty(documentSection) && (
                <>
                    <h2 className="font-size-title pt-4 pl-4">
                        <Trans>Document</Trans>
                    </h2>
                    {documentSection}
                </>
            )}
        </>
    );
}

interface FileFormFieldProps extends FormFieldProps {
    onFieldChange: (file: FileWithSource | undefined) => void;
}

function FileFormField({ onFieldChange, errorMessage, ...props }: FileFormFieldProps) {
    const { t } = useLingui();
    const [errorMsg, setErrorMsg] = useState<ReactNode>(undefined);
    const maxFileSizeInMB = 15;

    return (
        <FormField
            description={t`Please upload a copy of the business registration document or ID card for verification (max ${maxFileSizeInMB}MB).`}
            errorMessage={
                // NB: Parent can also pass in an error message
                errorMsg || errorMessage
            }
            {...props}
        >
            <FileWithSourceInput
                acceptedFileTypes={["image/*", "application/pdf"]}
                maxFileSizeInMB={maxFileSizeInMB}
                isDisabled={props.isDisabled}
                onChange={onFieldChange}
                onSetError={setErrorMsg}
            />
        </FormField>
    );
}

interface BFieldProps extends WTextFieldProps {
    name: EditableFieldName;
}

function BField(props: BFieldProps) {
    const { name } = props;
    //#region Hooks
    const fieldsConfig = useAtomValue(baFieldsConfigAtom);
    //#endregion

    const config = fieldsConfig[name];
    if (!config?.visible) {
        return null;
    }
    return <BFieldInner {...props} config={config} />;
}

function BFieldInner({ name, label, config, ...textFieldProps }: BFieldProps & { config: BillingAccountField }) {
    //#region Hooks
    const form = useFormContext<BillingFormInputs>();
    const { ref, props } = useAriaField(name, form, { isRequired: config.required });

    const { baFieldPresentation } = useThemeProps();
    //#endregion

    return (
        <WTextField
            type="text"
            label={label ?? baLabels[name]}
            {...props}
            {...baFieldPresentation?.[name]}
            {...textFieldProps}
        >
            <Input ref={ref} className={cn(FF.FormFieldInput, TF.Input)} />
        </WTextField>
    );
}

export function BillingCreate() {
    return (
        <>
            <CreateHero />
            <ContentLoadingSuspense>
                <BillingCreateForm />
            </ContentLoadingSuspense>
        </>
    );
}

export function BillingEdit({ baId }: { baId: BillingAccountId }) {
    const account = useBillingAccount(baId);
    return (
        <ContentLoadingSuspense>
            <BillingEditForm billingAccount={account} />
        </ContentLoadingSuspense>
    );
}

function CreateHero() {
    const { t } = useLingui();
    return (
        <HeroBlock title={t`Create new`} icon="jp-card-icon">
            <Trans>
                You can create multiple billing accounts, each linked to specific payment methods. This approach allows
                you to effectively separate and monitor expenses across different teams, departments, or projects.
            </Trans>
        </HeroBlock>
    );
}
